import type { HugeiconsProps } from '@hugeicons/react-native';
import type { IMemberProfileEdit, IProfileOptions } from '~/models/MemberProfileEdit';
import {
  ApproximatelyEqualCircleIcon,
  ApproximatelyEqualSquareIcon,
  BedDoubleIcon,
  BubbleChatFavouriteIcon,
  Calendar03Icon,
  EarthIcon,
  FavouriteIcon,
  Fire02Icon,
  InputShortTextIcon,
  LanguageSkillIcon,
  MenuTwoLineIcon,
  LocationUser02Icon,
  Menu02Icon,
  RulerIcon,
  Tag01Icon,
  TapeMeasureIcon,
  Target01Icon,
  WeightScaleIcon,
  WorkoutRunIcon,
} from '@hugeicons-pro/core-stroke-rounded';

/**
 * Tag field names in the profile edit form
 */
export type TagField = keyof Pick<
  IMemberProfileEdit,
  | 'SocialTags'
  | 'Genders'
  | 'InterestedIn'
  | 'Relationships'
  | 'SexualInterests'
  | 'LookingFor'
  | 'BodyTypes'
  | 'BodyTypesLiked'
  | 'BodyPartsLiked'
  | 'BodyHair'
  | 'FacialHair'
  | 'CommunityKinks'
  | 'Kinks'
  | 'Languages'
  | 'Ethnicities'
>;

/**
 * Check if a field is single-select based on backend configuration
 */
export function isSingleSelectField(
  field: TagField | null | undefined,
  profileOptions: IProfileOptions | undefined
): boolean {
  if (!field || !profileOptions?.SingleSelectTags) return false;
  return profileOptions.SingleSelectTags.includes(field);
}

/**
 * Check if a non-tag field is unsupported based on backend configuration
 * Tag fields signal unsupported status by being null, so this only applies to other fields
 */
export function isFieldUnsupported(
  field: string,
  profileOptions: IProfileOptions | undefined
): boolean {
  if (!profileOptions?.UnsupportedFields) return false;
  return profileOptions.UnsupportedFields.includes(field);
}

/**
 * Icon mapping for multi-select fields
 */
export const EDIT_PROFILE_MULTI_SELECT_ICON_BY_FIELD: Partial<
  Record<TagField, HugeiconsProps['icon']>
> = {
  SocialTags: Tag01Icon,
  Genders: LocationUser02Icon,
  InterestedIn: LocationUser02Icon,
  Relationships: FavouriteIcon,
  LookingFor: Target01Icon,
  SexualInterests: BedDoubleIcon,
  CommunityKinks: BubbleChatFavouriteIcon,
  Kinks: Fire02Icon,
  BodyTypes: TapeMeasureIcon,
  BodyTypesLiked: TapeMeasureIcon,
  BodyPartsLiked: WorkoutRunIcon,
  BodyHair: ApproximatelyEqualSquareIcon,
  FacialHair: ApproximatelyEqualCircleIcon,
  Languages: LanguageSkillIcon,
  Ethnicities: EarthIcon,
};

export const DEFAULT_MULTI_SELECT_ICON: HugeiconsProps['icon'] = Tag01Icon;

/**
 * Translation key mapping for multi-select fields
 * Reuses keys from profile.* for consistency across search, profile view, and edit profile
 */
export const MULTI_SELECT_LABEL_KEY_BY_FIELD: Partial<Record<TagField, string>> = {
  SocialTags: 'editProfile.identifyAs',
  Genders: 'profile.gender',
  InterestedIn: 'profile.interestedIn',
  Relationships: 'profile.relationshipStatus',
  LookingFor: 'profile.lookingFor',
  SexualInterests: 'profile.sexualInterests',
  CommunityKinks: 'profile.communityKinks',
  Kinks: 'profile.otherKinks',
  BodyTypes: 'editProfile.myBodyType',
  BodyTypesLiked: 'editProfile.bodyTypesLiked',
  BodyPartsLiked: 'editProfile.bodyPartsLiked',
  BodyHair: 'editProfile.myBodyHair',
  FacialHair: 'editProfile.myFacialHair',
  Languages: 'profile.languages',
  Ethnicities: 'profile.origin',
};

/**
 * Sections for organizing profile edit fields
 * Reuses section title keys from profile.* for consistency
 */
export interface IEditProfileSection {
  key: string;
  titleKey: string;
  fallbackTitle: string;
  fields: TagField[];
}

export const EDIT_PROFILE_SECTIONS: IEditProfileSection[] = [
  {
    key: 'identity',
    titleKey: 'profile.sections.identity',
    fallbackTitle: 'Identity & Background',
    fields: ['SocialTags', 'Genders', 'InterestedIn', 'Relationships', 'Languages', 'Ethnicities'],
  },
  {
    key: 'preferences',
    titleKey: 'profile.preferences',
    fallbackTitle: 'Preferences',
    fields: ['LookingFor', 'SexualInterests', 'CommunityKinks', 'Kinks'],
  },
  {
    key: 'bodyFeatures',
    titleKey: 'profile.sections.bodyFeatures',
    fallbackTitle: 'Body Features',
    fields: ['BodyTypes', 'BodyTypesLiked', 'BodyPartsLiked', 'BodyHair', 'FacialHair'],
  },
];

/**
 * Icon mapping for basic fields
 */
export const EDIT_PROFILE_BASIC_ICONS = {
  firstName: InputShortTextIcon,
  birthDate: Calendar03Icon,
  height: RulerIcon,
  weight: WeightScaleIcon,
  description: Menu02Icon,
  textField1: MenuTwoLineIcon,
  textField2: MenuTwoLineIcon,
  textField3: MenuTwoLineIcon,
};

/**
 * Translation section mapping for tag fields (used with domain translations)
 */
export const PROFILE_EDIT_TRANSLATION_MAP: Record<string, string> = {
  SocialTags: 'SocialTags',
  Genders: 'Other',
  InterestedIn: 'Other',
  SexualInterests: 'Other',
  CommunityKinks: 'Other',
  BodyPartsLiked: 'BodyFeatures',
  Kinks: 'Other',
  LookingFor: 'Interests',
  BodyTypes: 'BodyFeatures',
  BodyTypesLiked: 'BodyFeatures',
  Languages: 'Languages',
  Relationships: 'Other',
  Ethnicities: 'Ethnicities',
  BodyHair: 'BodyFeatures',
  FacialHair: 'BodyFeatures',
};

/**
 * Get available options for a multi-select field
 */
export function getOptionsForField(
  field: TagField,
  options: IProfileOptions | undefined
): string[] | null {
  if (!options) return null;
  const fieldOptions = options[field as keyof IProfileOptions];
  return Array.isArray(fieldOptions) && fieldOptions.length > 0 ? fieldOptions : null;
}
